import { Component } from '@theme/component';

/**
 * EEMENTOR hero slider: cross-fading slides with arrows, dots and a pause button.
 * Autoplay pauses while the pointer is over the slider or focus is inside it, stops
 * when the visitor pauses it, and never runs with reduced motion. Inactive slides are
 * inert so their links aren't reachable by keyboard.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} slides
 * @property {HTMLButtonElement[]} [dots]
 * @property {HTMLButtonElement} [pause]
 *
 * @extends {Component<Refs>}
 */
class HeroSliderComponent extends Component {
  requiredRefs = ['slides'];

  #index = 0;
  #timer = 0;
  #hovered = false;
  #focused = false;
  #userPaused = false;
  #abort = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#abort;
    this.addEventListener('pointerenter', () => ((this.#hovered = true), this.#schedule()), { signal });
    this.addEventListener('pointerleave', () => ((this.#hovered = false), this.#schedule()), { signal });
    this.addEventListener('focusin', () => ((this.#focused = true), this.#schedule()), { signal });
    this.addEventListener('focusout', (event) => {
      if (!this.contains(/** @type {Node|null} */ (event.relatedTarget))) {
        this.#focused = false;
        this.#schedule();
      }
    }, { signal });
    document.addEventListener('visibilitychange', () => this.#schedule(), { signal });
    this.#schedule();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort.abort();
    clearTimeout(this.#timer);
  }

  next() {
    this.#show(this.#index + 1);
  }

  previous() {
    this.#show(this.#index - 1);
  }

  /** @param {{ index?: string|number }} data */
  goTo(data) {
    this.#show(Number(data?.index ?? 0));
  }

  togglePlay() {
    this.#userPaused = !this.#userPaused;
    const { pause } = this.refs;
    if (pause) {
      pause.setAttribute('aria-label', (this.#userPaused ? pause.dataset.labelPlay : pause.dataset.labelPause) ?? '');
    }
    this.toggleAttribute('data-paused', this.#userPaused);
    this.#schedule();
  }

  /** @param {number} target */
  #show(target) {
    const { slides, dots } = this.refs;
    const count = slides.length;
    if (!count) return;
    this.#index = ((target % count) + count) % count;

    slides.forEach((slide, i) => {
      const active = i === this.#index;
      slide.classList.toggle('is-active', active);
      slide.inert = !active;
      if (active) slide.removeAttribute('aria-hidden');
      else slide.setAttribute('aria-hidden', 'true');
    });
    dots?.forEach((dot, i) => {
      if (i === this.#index) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
    this.#schedule();
  }

  #schedule() {
    clearTimeout(this.#timer);
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const enabled = this.dataset.autoplay === 'true' && this.refs.slides.length > 1;
    if (!enabled || reduceMotion || this.#userPaused || this.#hovered || this.#focused || document.hidden) return;
    const interval = Number(this.dataset.interval) || 7000;
    this.#timer = window.setTimeout(() => this.next(), interval);
  }
}

if (!customElements.get('ee-hero-slider')) {
  customElements.define('ee-hero-slider', HeroSliderComponent);
}
